import { clsx } from 'clsx'
import { useLatestCallback } from 'hook/useLatestCallback'
import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { parseIntFinite } from 'util/number'
import { styles } from 'util/style'
import css from './Input.module.scss'

type Props = {
  disabled?: boolean
  height?: string | number
  onChange(value: number | undefined): void
  placeholder?: string
  upper?: boolean
  value: number | null | undefined
}

export const InputNumber = (props: Props) => {
  const { onChange, disabled, placeholder, height, upper } = props
  const [version, setVersion] = useState(0)
  const [text, setText] = useState('')
  const [focus, setFocus] = useState(false)
  const ref = useRef<HTMLInputElement>()
  const timeout = useRef<TimeoutId>()
  const className = clsx(css.field, upper && css.upper, focus && css.focus)
  const style = styles({ height })
  const value = focus ? text : props.value?.toFixed() ?? ''

  function applyChange(text: string, silent?: boolean) {
    const value = parseIntFinite(text)
    onChange(value)
    if (!silent) setVersion(version + 1)
  }

  const updateText = useLatestCallback(() => {
    setText(props.value?.toFixed() ?? '')
  })

  useEffect(() => {
    if (version) updateText()
  }, [updateText, version])

  const onRef = useLatestCallback((element: HTMLInputElement | null) => {
    ref.current = element ?? undefined
  })

  const onTimeout = useLatestCallback(() => {
    applyChange(text, true)
  })

  const onFocus = useLatestCallback(() => {
    setFocus(true)
    setText(value)
  })

  const onBlur = useLatestCallback(() => {
    setFocus(false)
    applyChange(text)
    setText('')
  })

  const onKeyDown = useLatestCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      applyChange('')
      setText('')
    }
    if (event.key === 'Enter') {
      applyChange(text)
    }
  })

  const onChangeInner = useLatestCallback((event: FormEvent<HTMLInputElement>) => {
    const text = event.currentTarget.value
    setText(text.replace(/\D/g, ''))
    clearTimeout(timeout.current)
    timeout.current = setTimeout(onTimeout, 1000)
  })

  return <div className={className} style={style} onFocusCapture={onFocus} onBlurCapture={onBlur}>
    <input ref={onRef} className={css.input} type="text" inputMode="numeric" placeholder={placeholder}
      disabled={disabled} value={value} onChange={onChangeInner} onKeyDown={onKeyDown} />
  </div>
}
